import type { RuntimePermissionCheck, RuntimePermissionState } from '@/lib/runtime-permissions-plugin';

/** Capacitor / iOS permission strings, including Photos limited access. */
export function mapCapacitorPermissionState(state: string | undefined): RuntimePermissionState {
  if (state === 'granted' || state === 'limited') return 'granted';
  if (state === 'denied') return 'denied';
  return 'prompt';
}

export function isCapacitorPermissionGranted(state: string | undefined): boolean {
  return state === 'granted' || state === 'limited';
}

function loadCamera() {
  return import('@capacitor/camera');
}

function loadGeolocation() {
  return import('@capacitor/geolocation');
}

function loadPush() {
  return import('@capacitor/push-notifications');
}

async function checkMicrophoneState(): Promise<RuntimePermissionState> {
  try {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
      return 'prompt';
    }
    const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    return mapCapacitorPermissionState(status.state);
  } catch {
    return 'prompt';
  }
}

export async function checkCapacitorRuntimePermissions(): Promise<RuntimePermissionCheck | null> {
  try {
    const [{ Camera }, { Geolocation }, { PushNotifications }] = await Promise.all([
      loadCamera(),
      loadGeolocation(),
      loadPush(),
    ]);
    const [cam, geo, push, microphone] = await Promise.all([
      Camera.checkPermissions().catch(() => null),
      Geolocation.checkPermissions().catch(() => null),
      PushNotifications.checkPermissions().catch(() => null),
      checkMicrophoneState(),
    ]);
    const locationGranted =
      isCapacitorPermissionGranted(geo?.location) || isCapacitorPermissionGranted(geo?.coarseLocation);
    return {
      camera: mapCapacitorPermissionState(cam?.camera),
      photos: mapCapacitorPermissionState(cam?.photos),
      location: locationGranted ? 'granted' : mapCapacitorPermissionState(geo?.location || geo?.coarseLocation),
      microphone,
      notifications: mapCapacitorPermissionState(push?.receive),
    };
  } catch {
    return null;
  }
}

/**
 * Unexpected Capacitor failures must not look like an OS denial:
 * otherwise the wizard opens Settings before any system prompt (App Store 5.1.1(iv)).
 */
function fromCaughtRequest(): RuntimePermissionState {
  return 'prompt';
}

export async function requestCapacitorLocation(): Promise<RuntimePermissionState> {
  try {
    const { Geolocation } = await loadGeolocation();
    const perm = await Geolocation.requestPermissions();
    if (isCapacitorPermissionGranted(perm.location) || isCapacitorPermissionGranted(perm.coarseLocation)) {
      return 'granted';
    }
    return mapCapacitorPermissionState(perm.location || perm.coarseLocation);
  } catch {
    return fromCaughtRequest();
  }
}

export async function requestCapacitorCamera(): Promise<RuntimePermissionState> {
  try {
    const { Camera } = await loadCamera();
    const perm = await Camera.requestPermissions({ permissions: ['camera'] });
    return mapCapacitorPermissionState(perm.camera);
  } catch {
    return fromCaughtRequest();
  }
}

export async function requestCapacitorPhotos(): Promise<RuntimePermissionState> {
  try {
    const { Camera } = await loadCamera();
    const perm = await Camera.requestPermissions({ permissions: ['photos'] });
    return mapCapacitorPermissionState(perm.photos);
  } catch {
    return fromCaughtRequest();
  }
}

export async function requestCapacitorMicrophone(): Promise<RuntimePermissionState> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return fromCaughtRequest();
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
    return 'granted';
  } catch (error) {
    const name = error instanceof DOMException ? error.name : '';
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
      return 'denied';
    }
    return fromCaughtRequest();
  }
}

export async function requestCapacitorNotifications(): Promise<RuntimePermissionState> {
  try {
    const { PushNotifications } = await loadPush();
    const perm = await PushNotifications.requestPermissions();
    return mapCapacitorPermissionState(perm.receive);
  } catch {
    return fromCaughtRequest();
  }
}

export async function requestCapacitorPermissionStep(
  step: keyof RuntimePermissionCheck,
): Promise<RuntimePermissionState> {
  switch (step) {
    case 'location':
      return requestCapacitorLocation();
    case 'camera':
      return requestCapacitorCamera();
    case 'photos':
      return requestCapacitorPhotos();
    case 'microphone':
      return requestCapacitorMicrophone();
    case 'notifications':
      return requestCapacitorNotifications();
    default:
      return fromCaughtRequest();
  }
}

/** Post-denial only. `app-settings:` maps to the current app's Settings page on iOS. */
export async function openCapacitorAppSettings(): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    window.location.href = 'app-settings:';
  } catch {
    /* ignore */
  }
}
